import React, { useEffect, useMemo, useState } from 'react';
import SeminarCreateNewClinic from './SeminarCreateNewClinic';
import Loading from '../common/Loading';
import { ClinicLocation } from '@chiroup/core/types/Clinic.type';
import { FormErrors } from '@chiroup/core/types/ErrorResponse.type';
import { MeClinic } from '@chiroup/core/types/Me.type';
import {
  SeminarSignupForm,
  SeminarMeData,
} from '@chiroup/core/types/Seminar.type';
import { ValueOf } from '@visx/scale';

type Props = {
  value: Partial<SeminarSignupForm>;
  onChange: (
    key: keyof Partial<SeminarSignupForm>,
  ) => (val: ValueOf<SeminarSignupForm>) => void;
  userData: SeminarMeData;
  errors?: FormErrors;
  isSubmitting: boolean;
  setUserData: React.Dispatch<
    React.SetStateAction<
      { seminarMe: SeminarMeData; token: string } | undefined
    >
  >;
  onSubmit: ({
    email,
    validationCode,
  }: Partial<SeminarSignupForm>) => Promise<Partial<SeminarSignupForm>>;
};

const SeminarClinicForm: React.FC<Props> = ({
  value,
  onChange,
  userData,
  errors,
  isSubmitting,
  setUserData,
  onSubmit,
}) => {
  const [createClinic, setCreateClinic] = useState(false);
  const [submitClinic, setSubmitClinic] = useState<number | null>(null);

  const educationAccount = useMemo(() => {
    return (
      userData.clinics?.length &&
      userData.clinics?.every((clinic) => clinic.plan === 'education')
    );
  }, [userData.clinics]);

  const select = (clinic: MeClinic, index: number) => {
    setSubmitClinic(index);
    const primaryLocation = clinic.locations.find(
      (location: ClinicLocation) => location.primary,
    );
    setUserData(
      (prevState: { seminarMe: SeminarMeData; token: string } | undefined) => {
        if (!prevState) return undefined;

        return {
          ...prevState,
          seminarMe: {
            ...prevState.seminarMe,
            selectedClinic: clinic,
            selectedLocation: primaryLocation,
          },
        };
      },
    );

    onChange('clinicID')(primaryLocation?.clinicId ?? clinic.ID);
    onChange('name')(primaryLocation?.name ?? clinic.name);
    onChange('address1')(primaryLocation?.address1 ?? clinic.address1);
    onChange('city')(primaryLocation?.city ?? clinic.city);
    onChange('state')(primaryLocation?.state ?? clinic.state);
    onChange('zip')(primaryLocation?.zip ?? clinic.zip);
    onChange('locationID')(primaryLocation?.ID);
  };

  useEffect(() => {
    if (value.clinicID) {
      onSubmit(value);
    }
  }, [value.clinicID]);

  return (
    <div className="flex flex-col">
      <div className="sm:mx-auto sm:w-full ">
        <h2 className="mt-6 text-3xl font-extrabold leading-9 text-center text-gray-900 dark:text-darkGray-50">
          {userData.clinics?.length && !createClinic
            ? 'Select a clinic'
            : 'Add a new clinic'}
        </h2>
        {!educationAccount && !createClinic && !!userData.clinics?.length && (
          <p className="text-sm leading-5 text-center text-gray-600 max-w">
            Or{' '}
            <span
              onClick={() => setCreateClinic(true)}
              className="font-medium transition duration-150 ease-in-out cursor-pointer text-primary-600 hover:text-primary-400 focus:outline-none focus:underline"
            >
              create a new clinic
            </span>
          </p>
        )}
      </div>

      {createClinic || !userData.clinics?.length ? (
        <SeminarCreateNewClinic
          errors={errors}
          onChange={onChange}
          value={value}
          isSubmitting={isSubmitting}
          setCreateClinic={setCreateClinic}
        />
      ) : (
        <div className="mt-8 overflow-hidden bg-white shadow dark:bg-darkGray-800 sm:rounded-md">
          <ul>
            {userData.clinics?.map((clinic, i) => {
              const disabled =
                clinic.plan === 'trial'
                  ? false
                  : clinic.subscription.status === 'active' &&
                      clinic.subscription.plan !== 'trial'
                    ? true
                    : false;

              const primaryLocation = clinic.locations.find(
                (location) => location.primary,
              ) || {
                logo: '',
                name: 'Unknown name',
              };
              return (
                <li
                  key={i}
                  className={[
                    `${
                      !disabled
                        ? 'hover:bg-gray-50 dark:hover:bg-darkGray-700'
                        : ''
                    } focus:outline-none focus:bg-gray-50 dark:focus:bg-darkGray-700 transition duration-150 ease-in-out ${
                      disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                    }`,
                    i
                      ? 'border-t border-gray-300 dark:border-darkGray-600'
                      : '',
                  ].join(' ')}
                  onClick={() => {
                    if (!disabled) select(clinic, i);
                  }}
                >
                  <div className="flex items-center px-4 py-4 sm:px-6">
                    <div className="flex items-center flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        {primaryLocation.logo ? (
                          <img
                            className="w-12 h-auto"
                            src={primaryLocation.logo}
                            alt=""
                          />
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-12 h-12 text-gray-400 dark:text-darkGray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 px-4 md:grid md:grid-cols-2 md:gap-4">
                        <div>
                          <div
                            className={`text-sm ${
                              disabled ? 'font-light' : 'font-medium'
                            } leading-5 truncate ${
                              disabled ? 'text-gray-400' : 'text-primary-600'
                            }`}
                          >
                            {primaryLocation.name}
                          </div>
                        </div>
                      </div>
                    </div>
                    {!disabled && (
                      <div>
                        {submitClinic !== i ? (
                          <svg
                            className="w-5 h-5 text-gray-400 dark:text-darkGray-200"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <Loading className="w-5 h-5" color="text-gray-400" />
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SeminarClinicForm;
